import { useState, useRef, useCallback, useEffect } from "react";

/**
 * Hook that manages a value that auto-resets to its initial state after a delay.
 * @param {any} initialValue - The initial/reset value (stable reference required for objects/arrays)
 * @param {number} delay - Delay in ms before auto-reset (default: 5000)
 * @returns {[any, function, function]} - [value, setValue, clearValue]
 */
const useAutoReset = (initialValue, delay = 5000) => {
  const [value, setValueInternal] = useState(initialValue);
  const timeoutRef = useRef(null);
  const initialValueRef = useRef(initialValue);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const setValue = useCallback(
    (newValue) => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);

      setValueInternal(newValue);

      timeoutRef.current = setTimeout(() => {
        setValueInternal(initialValueRef.current);
      }, delay);
    },
    [delay]
  );

  const clearValue = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setValueInternal(initialValueRef.current);
  }, []);

  return [value, setValue, clearValue];
};

export default useAutoReset;
